import {createContext, ReactNode, useContext, FC} from 'react';
import {useActor, useInterpret, useSelector} from '@xstate/react';
import {uiMachine} from '../machines/uiMachine';
import {InterpreterFrom, StateFrom} from 'xstate';

type UiServiceType = InterpreterFrom<typeof uiMachine>;
type UIState = StateFrom<typeof uiMachine>;

interface PlayerStateContextType {
  uiService: UiServiceType;
}

export const PlayerStateContext = createContext<PlayerStateContextType>(
  // This is weird, but is how the xstate team recommends typing this
  {} as PlayerStateContextType
);

interface PlayerStateProviderProps {
  children: ReactNode;
}

export const PlayerStateProvider: FC<PlayerStateProviderProps> = ({
  children,
}) => {
  const uiService = useInterpret(uiMachine);

  return (
    <PlayerStateContext.Provider value={{uiService}}>
      {children}
    </PlayerStateContext.Provider>
  );
};

export const useUiState = (): ReturnType<typeof useActor<UiServiceType>> => {
  return useActor(useContext(PlayerStateContext).uiService);
};

export const useUiService = (): UiServiceType => {
  return useContext(PlayerStateContext).uiService;
};

export const useIsLoaded = (): boolean => {
  const sourceLoadedSelector = (state: UIState): boolean => {
    return state.matches('sourceLoaded');
  };

  const uiServices = useContext(PlayerStateContext);
  return useSelector(uiServices.uiService, sourceLoadedSelector);
};

export const useIsPlaying = (): boolean => {
  const playingSelector = (state: UIState): boolean => {
    return state.matches({sourceLoaded: {playback: {status: 'playing'}}});
  };

  const uiServices = useContext(PlayerStateContext);
  return useSelector(uiServices.uiService, playingSelector);
};

export const useIsPaused = (): boolean => {
  const pausedSelector = (state: UIState): boolean => {
    return state.matches({sourceLoaded: {playback: {status: 'paused'}}});
  };

  const uiServices = useContext(PlayerStateContext);
  return useSelector(uiServices.uiService, pausedSelector);
};

export const useHasPlayed = (): boolean => {
  const [state] = useUiState();
  return state.context.hasPlayed;
};

export const useIsMuted = (): boolean => {
  const mutedSelector = (state: UIState): boolean => {
    return state.matches({sourceLoaded: {muteStatus: 'muted'}});
  };

  const uiServices = useContext(PlayerStateContext);
  return useSelector(uiServices.uiService, mutedSelector);
};

export const useIsSeeking = (): boolean => {
  const selector = (state: UIState): boolean =>
    state.matches({sourceLoaded: {playback: 'seeking'}});

  const {uiService} = useContext(PlayerStateContext);

  return useSelector(uiService, selector);
};

export const useVolume = (): number => {
  const [state] = useUiState();
  return state.context.volume;
};

export const useIsFullscreen = (): boolean => {
  const fullscreenSelector = (state: StateFrom<typeof uiMachine>): boolean => {
    return state.matches({sourceLoaded: {fullscreenStatus: 'fullscreen'}});
  };

  const uiServices = useContext(PlayerStateContext);
  return useSelector(uiServices.uiService, fullscreenSelector);
};
